import React, { useState, useEffect, useRef, useMemo } from 'react';
import Icon from '../ui/Icon';
import { T, STATUS_OPTIONS, SOURCE_OPTIONS, TYPE_OPTIONS } from '../constants/mockData';
import { ticketsAPI, usersAPI, groupsAPI, masterAPI } from '../services/api';

export default function CreateTicketModal({ onClose, onSave }) {
  const [form, setForm] = useState({
    requester: "", email: "", subject: "", description: "",
    priority: "Low", status: "Open", dept: "",
    assignedToId: null, assignedToName: "",
    source: "Email", type: "Incident",
    groupId: null, groupName: "",
    category: "", subCategory: "", headOfDept: "", orgUnit: "",
    requesterId: null,
  });
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [agents, setAgents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [requesters, setRequesters] = useState([]);     // all users available as requesters
  const [masterData, setMasterData] = useState({});     // { category:[], sub_category:[], department:[], head_of_department:[], org_unit:[] }
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // ── Requester combobox state ──
  const [reqQuery, setReqQuery] = useState('');
  const [reqOpen, setReqOpen] = useState(false);
  const reqRef = useRef(null);

  useEffect(() => {
    usersAPI.list()
      .then(data => {
        const all = data || [];
        setAgents(all.filter(u => u.role === 'agent' || u.role === 'admin'));
        // Anyone can be a requester — show all users
        setRequesters(all);
      })
      .catch(() => {});
    groupsAPI.list().then(data => setGroups(data || [])).catch(() => {});
    masterAPI.all()
      .then(rows => {
        const grouped = {};
        (rows || []).forEach(r => {
          if (!grouped[r.field_type]) grouped[r.field_type] = [];
          grouped[r.field_type].push(r);
        });
        setMasterData(grouped);
      })
      .catch(() => {});
  }, []);

  // Close requester dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (reqRef.current && !reqRef.current.contains(e.target)) setReqOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Filter requesters by typed query
  const filteredRequesters = useMemo(() => {
    const q = reqQuery.trim().toLowerCase();
    if (!q) return requesters.slice(0, 50);
    return requesters
      .filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [reqQuery, requesters]);

  // Filter sub_categories by selected category (parent_value)
  const filteredSubCategories = useMemo(() => {
    const subs = masterData.sub_category || [];
    if (!form.category) return subs;
    return subs.filter(s => !s.parent_value || s.parent_value === form.category);
  }, [form.category, masterData.sub_category]);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: false }));
  };

  // When a known requester is picked → autofill name/email/department
  const pickRequester = (r) => {
    setForm(f => ({
      ...f,
      requesterId: r.id,
      requester: r.name || '',
      email: r.email || '',
      dept: r.department || f.dept,  // some user objects may include department
    }));
    setReqQuery(r.name || '');
    setReqOpen(false);
    if (errors.requester) setErrors(e => ({ ...e, requester: false }));
  };

  const priorityColors = {
    Low: { bg: "#EBF0FF", color: "#3B5BDB", border: "#C7D2FE" },
    Medium: { bg: "#1c1505", color: "#fbbf24", border: "#78350f" },
    High: { bg: "#FFF5F5", color: "#f87171", border: "#FED7D7" }
  };

  const fieldStyle = (key) => ({
    width: "100%",
    padding: "10px 13px",
    border: `1.5px solid ${errors[key] ? "#C53030" : focusedField === key ? T.active : T.border}`,
    borderRadius: 8,
    fontSize: 13,
    color: "#1A202C",
    background: focusedField === key ? "#EBF0FF" : T.g50,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
    transition: "border-color 0.15s, background 0.15s",
    boxShadow: focusedField === key ? `0 0 0 3px ${T.g100}` : "none",
  });

  const lStyle = {
    fontSize: 10,
    fontWeight: 700,
    color: T.muted,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: 6,
    display: "flex",
    alignItems: "center",
    gap: 4,
  };

  const handleSave = async () => {
    const newErrors = {};
    if (!form.requester.trim()) newErrors.requester = true;
    if (!form.subject.trim()) newErrors.subject = true;
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        subject: form.subject,
        description: form.description,
        priority: form.priority,
        status: form.status,
        type: form.type,
        source: form.source,
        requester_name: form.requester,
        requester_email: form.email || undefined,
        assigned_to: form.assignedToId || undefined,
        assigned_group: form.groupId || undefined,
        department: form.dept || undefined,
        category: form.category || undefined,
        sub_category: form.subCategory || undefined,
        head_of_department: form.headOfDept || undefined,
        org_unit: form.orgUnit || undefined,
      };
      const created = await ticketsAPI.create(payload);
      onSave?.(created);
      onClose();
    } catch (err) {
      setSaveError(err.message || 'Failed to create ticket');
    } finally {
      setSaving(false);
    }
  };

  const SectionDivider = ({ label }) => (
    <div style={{ gridColumn: "span 2", display: "flex", alignItems: "center", gap: 10, margin: "4px 0 2px" }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: T.active, textTransform: "uppercase", letterSpacing: "0.1em", whiteSpace: "nowrap" }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${T.border}, transparent)` }} />
    </div>
  );

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(5,46,22,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(2px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: "#FFFFFF", borderRadius: 16, width: window.innerWidth < 768 ? "95%" : 580, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 32px 80px rgba(5,46,22,0.22), 0 0 0 1px rgba(5,46,22,0.06)", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 18px", borderBottom: `1px solid ${T.border}`, background: `linear-gradient(135deg, ${T.g50} 0%, #fff 60%)`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: T.active, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(22,163,74,0.35)" }}>
                <Icon name="ticket" size={16} color="#fff" />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#1A202C", lineHeight: 1.2 }}>Create New Ticket</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>Fill in the details below to submit a support ticket</div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ width: 30, height: 30, border: `1px solid ${T.border}`, borderRadius: 7, background: "#FFFFFF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.background = T.g50}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}
            >
              <Icon name="close" size={14} color={T.muted} />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: "auto", flex: 1, padding: window.innerWidth < 768 ? "16px" : "20px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: "14px 16px" }}>
            {/* Requester info section */}
            <SectionDivider label="Requester Info" />

            <div style={{ gridColumn: "span 2", position: "relative" }} ref={reqRef}>
              <label style={lStyle}>
                Requester Name <span style={{ color: "#ef4444" }}>*</span>
                <span style={{ color: T.muted, fontWeight: 500, marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>
                  — pick existing or type a new name
                </span>
                {errors.requester && <span style={{ color: "#ef4444", fontSize: 10, fontWeight: 600, marginLeft: 4 }}>Required</span>}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  style={{ ...fieldStyle("requester"), paddingRight: 32, ...(errors.requester ? { borderColor: "#C53030", boxShadow: "0 0 0 3px #fee2e2" } : {}) }}
                  value={form.requester}
                  onChange={e => {
                    set("requester", e.target.value);
                    setReqQuery(e.target.value);
                    setReqOpen(true);
                    // typed-over a previously linked requester → unlink
                    if (form.requesterId) setForm(f => ({ ...f, requesterId: null }));
                  }}
                  onFocus={() => { setFocusedField("requester"); setReqOpen(true); }}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Search by name or email…"
                  autoComplete="off"
                />
                <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                  <Icon name="chevronD" size={14} color={T.muted} />
                </div>
              </div>

              {reqOpen && filteredRequesters.length > 0 && (
                <div
                  style={{
                    position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4,
                    background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: 8,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.18)", zIndex: 50,
                    maxHeight: 240, overflowY: "auto",
                  }}
                >
                  {filteredRequesters.map(r => (
                    <button
                      key={r.id}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); pickRequester(r); }}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        width: "100%", padding: "9px 12px", background: "transparent", border: "none",
                        borderBottom: `1px solid ${T.border}`, textAlign: "left", cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = T.g50}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1A202C" }}>{r.name}</span>
                        <span style={{ fontSize: 11, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {r.email}{r.department ? ` · ${r.department}` : ''}
                        </span>
                      </div>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10,
                        background: r.role === 'admin' ? '#fef3c7' : r.role === 'agent' ? '#dbeafe' : '#e5e7eb',
                        color: r.role === 'admin' ? '#92400e' : r.role === 'agent' ? '#1e40af' : '#374151',
                        textTransform: 'uppercase', letterSpacing: '0.05em',
                      }}>{r.role || 'user'}</span>
                    </button>
                  ))}
                </div>
              )}
              {reqOpen && filteredRequesters.length === 0 && reqQuery.trim() && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4,
                  background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: 8,
                  padding: "10px 12px", fontSize: 12, color: T.muted, zIndex: 50,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                }}>
                  No match — “{reqQuery}” will be saved as a new requester name.
                </div>
              )}
            </div>

            <div style={{ gridColumn: "span 2" }}>
              <label style={lStyle}>Email Address</label>
              <input
                style={fieldStyle("email")}
                value={form.email}
                onChange={e => set("email", e.target.value)}
                onFocus={() => setFocusedField("email")}
                onBlur={() => setFocusedField(null)}
                placeholder="email@example.com"
                type="email"
              />
            </div>

            {/* Ticket details section */}
            <SectionDivider label="Ticket Details" />

            <div style={{ gridColumn: "span 2" }}>
              <label style={lStyle}>
                Subject <span style={{ color: "#ef4444" }}>*</span>
                {errors.subject && <span style={{ color: "#ef4444", fontSize: 10, fontWeight: 600, marginLeft: 4 }}>Required</span>}
              </label>
              <input
                style={{ ...fieldStyle("subject"), ...(errors.subject ? { borderColor: "#C53030", boxShadow: "0 0 0 3px #fee2e2" } : {}) }}
                value={form.subject}
                onChange={e => set("subject", e.target.value)}
                onFocus={() => setFocusedField("subject")}
                onBlur={() => setFocusedField(null)}
                placeholder="Brief description of the issue"
              />
            </div>

            <div style={{ gridColumn: "span 2" }}>
              <label style={lStyle}>Description</label>
              <textarea
                style={{ ...fieldStyle("description"), height: 90, resize: "vertical", lineHeight: 1.5 }}
                value={form.description}
                onChange={e => set("description", e.target.value)}
                onFocus={() => setFocusedField("description")}
                onBlur={() => setFocusedField(null)}
                placeholder="Provide a detailed description of the issue..."
              />
            </div>

            {/* Priority - styled pill selector */}
            <div>
              <label style={lStyle}>Priority</label>
              <div style={{ display: "flex", gap: 6 }}>
                {["Low", "Medium", "High"].map(p => {
                  const c = priorityColors[p];
                  const isSelected = form.priority === p;
                  return (
                    <button
                      key={p}
                      onClick={() => set("priority", p)}
                      style={{
                        flex: 1, padding: "8px 4px", borderRadius: 8, border: `1.5px solid ${isSelected ? c.border : T.border}`,
                        background: isSelected ? c.bg : T.g50,
                        color: isSelected ? c.color : T.muted,
                        fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.15s",
                        boxShadow: isSelected ? `0 0 0 2px ${c.border}` : "none",
                      }}
                    >
                      <div style={{ width: 7, height: 7, borderRadius: "50%", background: isSelected ? c.color : "#C8CDD6", margin: "0 auto 4px" }} />
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label style={lStyle}>Status</label>
              <select style={fieldStyle("status")} value={form.status} onChange={e => set("status", e.target.value)} onFocus={() => setFocusedField("status")} onBlur={() => setFocusedField(null)}>
                {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <label style={lStyle}>Source</label>
              <select style={fieldStyle("source")} value={form.source} onChange={e => set("source", e.target.value)} onFocus={() => setFocusedField("source")} onBlur={() => setFocusedField(null)}>
                {SOURCE_OPTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <label style={lStyle}>Type</label>
              <select style={fieldStyle("type")} value={form.type} onChange={e => set("type", e.target.value)} onFocus={() => setFocusedField("type")} onBlur={() => setFocusedField(null)}>
                {TYPE_OPTIONS.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>

            {/* Assignment section */}
            <SectionDivider label="Assignment" />

            <div>
              <label style={lStyle}>Group</label>
              <select
                style={fieldStyle("group")}
                value={form.groupId || ''}
                onChange={e => {
                  const g = groups.find(x => String(x.id) === e.target.value);
                  set('groupId', g ? g.id : null);
                  set('groupName', g ? g.name : '');
                }}
                onFocus={() => setFocusedField("group")}
                onBlur={() => setFocusedField(null)}
              >
                <option value="">— No Group —</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>

            <div>
              <label style={lStyle}>Assigned To</label>
              <select
                style={fieldStyle("assignedTo")}
                value={form.assignedToId || ''}
                onChange={e => {
                  const a = agents.find(x => String(x.id) === e.target.value);
                  set('assignedToId', a ? a.id : null);
                  set('assignedToName', a ? a.name : '');
                }}
                onFocus={() => setFocusedField("assignedTo")}
                onBlur={() => setFocusedField(null)}
              >
                <option value="">— Unassigned —</option>
                {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>

            {/* Categorization section */}
            <SectionDivider label="Categorization" />

            <div>
              <label style={lStyle}>Department</label>
              <select
                style={fieldStyle("dept")}
                value={form.dept}
                onChange={e => set("dept", e.target.value)}
                onFocus={() => setFocusedField("dept")}
                onBlur={() => setFocusedField(null)}
              >
                <option value="">— Select —</option>
                {(masterData.department || []).map(d => (
                  <option key={d.id} value={d.value}>{d.value}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={lStyle}>Head of Department</label>
              <select
                style={fieldStyle("headOfDept")}
                value={form.headOfDept}
                onChange={e => set("headOfDept", e.target.value)}
                onFocus={() => setFocusedField("headOfDept")}
                onBlur={() => setFocusedField(null)}
              >
                <option value="">— Select —</option>
                {(masterData.head_of_department || []).map(d => (
                  <option key={d.id} value={d.value}>{d.value}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={lStyle}>Category</label>
              <select
                style={fieldStyle("category")}
                value={form.category}
                onChange={e => {
                  set("category", e.target.value);
                  // reset subcategory if it doesn't belong to new category
                  set("subCategory", "");
                }}
                onFocus={() => setFocusedField("category")}
                onBlur={() => setFocusedField(null)}
              >
                <option value="">— Select —</option>
                {(masterData.category || []).map(c => (
                  <option key={c.id} value={c.value}>{c.value}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={lStyle}>
                Subcategory
                {form.category && filteredSubCategories.length === 0 && (
                  <span style={{ color: T.muted, fontWeight: 500, marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>
                    — none for “{form.category}”
                  </span>
                )}
              </label>
              <select
                style={fieldStyle("subCategory")}
                value={form.subCategory}
                onChange={e => set("subCategory", e.target.value)}
                onFocus={() => setFocusedField("subCategory")}
                onBlur={() => setFocusedField(null)}
                disabled={!form.category && (masterData.sub_category || []).length === 0}
              >
                <option value="">— Select —</option>
                {filteredSubCategories.map(s => (
                  <option key={s.id} value={s.value}>{s.value}</option>
                ))}
              </select>
            </div>

            <div style={{ gridColumn: "span 2" }}>
              <label style={lStyle}>Organisation Unit</label>
              <select
                style={fieldStyle("orgUnit")}
                value={form.orgUnit}
                onChange={e => set("orgUnit", e.target.value)}
                onFocus={() => setFocusedField("orgUnit")}
                onBlur={() => setFocusedField(null)}
              >
                <option value="">— Select —</option>
                {(masterData.org_unit || []).map(o => (
                  <option key={o.id} value={o.value}>{o.value}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: `1px solid ${T.border}`, background: T.g50, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.muted }}>
            {saveError ? (
              <span style={{ color: '#dc2626', fontWeight: 600 }}>{saveError}</span>
            ) : (
              <>
                <Icon name="info" size={13} color={T.muted} />
                Fields marked <span style={{ color: "#ef4444", fontWeight: 700 }}>*</span> are required
              </>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={onClose}
              style={{ padding: "9px 20px", borderRadius: 8, border: `1.5px solid ${T.border}`, background: "#FFFFFF", fontSize: 13, fontWeight: 600, color: T.muted, cursor: "pointer", transition: "all 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.border2; e.currentTarget.style.color = "#1A202C"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ padding: "9px 22px", borderRadius: 8, border: "none", background: saving ? '#718096' : 'var(--accent)', color: "#fff", fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: saving ? 'none' : "0 2px 8px var(--accent-bg)", transition: "all 0.15s" }}
              onMouseEnter={e => { if (!saving) { e.currentTarget.style.background = 'var(--accent-dark)'; e.currentTarget.style.boxShadow = "0 4px 14px var(--accent-bg)"; } }}
              onMouseLeave={e => { if (!saving) { e.currentTarget.style.background = 'var(--accent)'; e.currentTarget.style.boxShadow = "0 2px 8px var(--accent-bg)"; } }}
            >
              <Icon name="ticket" size={14} color="#fff" />
              {saving ? 'Creating…' : 'Create Ticket'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
